import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import TransitionNotice from '../common/TransitionNotice';

interface BillsSummaryProps {
  totalBills: number;
  totalAmountDue: number;
}

const BillsSummary: React.FC<BillsSummaryProps> = ({ totalBills, totalAmountDue }) => {
  const { theme } = useTheme();
  const isStLuke = theme.client === 'stluke';
  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);

  return (
    <div className="bills-summary-section mb-6 w-full">
      {/* Go-live notice, repeated here on purpose: a patient who followed a link with gn/lastName in
          the query string skips the verification modal entirely and lands straight on this page, so
          the modal mount alone would miss them. Self-gating per tenant. */}
      <div className="mb-6">
        <TransitionNotice theme={theme} />
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Bills</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 w-full">
        {/* Total Bills Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 hover:shadow-lg transition-all duration-300 hover:scale-105 min-h-[160px]">
          <div className="flex items-center h-full">
            <div className={`w-20 h-20 rounded-xl flex items-center justify-center mr-8 flex-shrink-0 ${isStLuke ? 'bg-[#F2F5F9]' : 'bg-purple-100'}`}>
              <svg 
                className={`w-10 h-10 ${isStLuke ? 'text-[#71001F]' : 'text-purple-600'}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-base font-medium text-gray-600 mb-3 uppercase tracking-wide">Total Bills</p>
              <p className="text-5xl font-bold text-gray-900">{totalBills}</p>
              <p className="text-sm text-gray-500 mt-2">Pending payment</p>
            </div>
          </div>
        </div>

        {/* Total Amount Due Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 hover:shadow-lg transition-all duration-300 hover:scale-105 min-h-[160px]">
          <div className="flex items-center h-full">
            <div className={`w-20 h-20 rounded-xl flex items-center justify-center mr-8 flex-shrink-0 ${isStLuke ? 'bg-[#F2F5F9]' : 'bg-orange-100'}`}>
              <span className={`text-4xl font-bold ${isStLuke ? 'text-[#71001F]' : 'text-orange-600'}`}>$</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-base font-medium text-gray-600 mb-3 uppercase tracking-wide">Total Amount Due</p>
              {/* Show the currency symbol once (icon at left), so format the amount without $ */}
              <p className="text-5xl font-bold text-gray-900">{formatAmount(totalAmountDue)}</p>
              <p className="text-sm text-gray-500 mt-2">Outstanding balance</p>
              <p className="text-xs text-gray-400 mt-1">
                Pending payment is being processed. Please allow 2-3 days to reflect.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillsSummary;
